import { State } from 'modules';
import {
  AccessLevel,
  MemberModel,
  NotificationEmailFrequency,
  TeamModel,
} from 'types/models';
import { getMyId } from './session';

import MemberFeatures from 'common/data/member-features';

import { MemberFeature } from 'types';
import { idToDate } from 'util/dates';
import getMs from 'util/get-ms';

export const getMemberById = (
  state: State,
  idMember: string,
): MemberModel | undefined => state.models.members[idMember];

export const getMembersByIds = (
  state: State,
  idMembers: string[] = [],
): MemberModel[] =>
  idMembers.reduce(
    (result, idMember) => {
      const member = getMemberById(state, idMember);
      if (member) {
        result.push(member);
      }

      return result;
    },
    [] as MemberModel[],
  );

export const getMemberByFullName = (state: State, fullName: string) =>
  Object.values(state.models.members).find(
    (member: MemberModel) => member.name === fullName,
  );

export const getMemberByIdMemberOrUsername = (
  state: State,
  idMemberOrUserName: string,
) =>
  Object.values(state.models.members).find(
    (member: MemberModel) =>
      member.username === idMemberOrUserName ||
      member.id === idMemberOrUserName,
  );

export const getMe = (state: State): MemberModel | undefined =>
  state.models.members[getMyId(state)];

export const isFeatureEnabled = (
  state: State,
  feature: MemberFeature,
  member: MemberModel | undefined = getMe(state),
): boolean => {
  if (member && member.products) {
    return MemberFeatures.isFeatureEnabled(feature, member.products);
  } else {
    return false;
  }
};

export const getMyLocalePreference = (state: State) => {
  const me = getMe(state);

  return me && me.prefs && me.prefs.locale ? me.prefs.locale : 'en-US';
};

export const isColorBlind = (state: State): boolean => {
  const user = getMe(state);

  return user && user.prefs ? user.prefs.colorBlind : false;
};

export const hasGold = (state: State, member?: MemberModel): boolean =>
  isFeatureEnabled(state, MemberFeature.Crown, member);

export const isTeamAdmin = (state: State, team: TeamModel | null): boolean => {
  const idMe = getMyId(state);
  const teamAdminIds =
    !!team &&
    team.memberships &&
    team.memberships.map(
      (membership) =>
        membership.type === 'admin' ? membership.idMember : undefined,
    );

  return teamAdminIds && !!teamAdminIds.length && teamAdminIds.includes(idMe);
};

export const isVirtualMember = (state: State, member: MemberModel | string) => {
  const m = typeof member === 'string' ? getMemberById(state, member) : member;

  return !!m && m.memberType === AccessLevel.Ghost;
};

export const getNotificationEmailFrequency = (state: State) => {
  const me = getMe(state);

  if (!me || !me.prefs) {
    return NotificationEmailFrequency.Instantly;
  }

  return me.prefs.minutesBetweenSummaries;
};

export const getDismissedMessage = (state: State, name: string) => {
  const me = getMe(state);
  if (!me) {
    return null;
  }

  const messagesDismissed = me.messagesDismissed || [];

  return messagesDismissed.find((message) => message.name === name);
};

export const shouldShowMeMarketingOptIn = (state: State) => {
  const me = getMe(state);
  if (!me) {
    return false;
  }

  const isConfirmed = !!me.confirmed;

  const isMoreThanThreeDaysOld =
    new Date().getTime() - idToDate(me.id).getTime() > getMs({ days: 3 });

  const hasSetMarketingOptIn =
    me.marketingOptIn && me.marketingOptIn.hasOwnProperty('date');

  return isConfirmed && isMoreThanThreeDaysOld && !hasSetMarketingOptIn;
};

export const shouldShowMeNoticeOfTosChange = (state: State) => {
  const me = getMe(state);
  if (!me) {
    return false;
  }

  const dismissedMessage = getDismissedMessage(
    state,
    '1-nov-2018-tos-change-accepted',
  );

  //  Show notice of ToS change if the user's account was created before
  //  November 1, 2018 and the user has not already dismissed the message by
  //  clicking "I Agree"
  return new Date(2018, 10, 1) > idToDate(me.id) && !dismissedMessage;
};



// WEBPACK FOOTER //
// ./app/gamma/src/selectors/members.ts